import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock, Target, TrendingUp, BookOpen, Code, Briefcase, CheckCircle } from 'lucide-react';

const RoadmapRefinementModal = ({ isOpen, onClose, onSubmit, careerPath }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({
    timeline: '',
    learningStyle: '',
    timeCommitment: '',
    experienceLevel: '',
    primaryGoal: '',
    preferredFormat: '',
    techStack: '',
    budget: '',
    constraints: ''
  });

  const questions = [
    {
      id: 'timeline',
      title: 'What\'s your target timeline?',
      subtitle: 'How quickly do you want to achieve this career transition?',
      icon: Clock,
      options: [
        { value: '3-months', label: '3 Months', desc: 'Intensive learning, full-time commitment' },
        { value: '6-months', label: '6 Months', desc: 'Balanced pace, part-time learning' },
        { value: '12-months', label: '12 Months', desc: 'Steady progress, flexible schedule' },
        { value: 'flexible', label: 'Flexible', desc: 'No strict deadline, learn at my pace' }
      ]
    },
    {
      id: 'timeCommitment',
      title: 'How much time can you dedicate weekly?',
      subtitle: 'This helps us pace your learning journey',
      icon: Target,
      options: [
        { value: '5-10', label: '5-10 hours/week', desc: 'Light commitment, weekend learning' },
        { value: '10-15', label: '10-15 hours/week', desc: 'Moderate, 2 hours daily' },
        { value: '15-25', label: '15-25 hours/week', desc: 'Serious commitment, part-time' },
        { value: '25+', label: '25+ hours/week', desc: 'Full-time learning mode' }
      ]
    },
    {
      id: 'experienceLevel',
      title: 'What\'s your current level in this field?',
      subtitle: 'We\'ll tailor the difficulty to match your background',
      icon: TrendingUp,
      options: [
        { value: 'complete-beginner', label: 'Complete Beginner', desc: 'Starting from scratch' },
        { value: 'some-basics', label: 'Know Some Basics', desc: 'Familiar with core concepts' },
        { value: 'intermediate', label: 'Intermediate', desc: 'Have some practical experience' },
        { value: 'advanced', label: 'Advanced', desc: 'Looking to specialize/level up' }
      ]
    },
    {
      id: 'learningStyle',
      title: 'How do you learn best?',
      subtitle: 'We\'ll prioritize resources that match your style',
      icon: BookOpen,
      options: [
        { value: 'video', label: 'Video Tutorials', desc: 'Visual demonstrations and courses' },
        { value: 'reading', label: 'Reading & Documentation', desc: 'Books, articles, and guides' },
        { value: 'hands-on', label: 'Hands-on Projects', desc: 'Learning by building things' },
        { value: 'mixed', label: 'Mixed Approach', desc: 'Combination of all methods' }
      ]
    },
    {
      id: 'primaryGoal',
      title: 'What\'s your primary goal?',
      subtitle: 'This helps us focus your learning path',
      icon: Target,
      options: [
        { value: 'job-switch', label: 'Switch Jobs', desc: 'Get hired in a new role soon' },
        { value: 'skill-up', label: 'Skill Up', desc: 'Advance in current career' },
        { value: 'side-project', label: 'Side Project', desc: 'Build something on the side' },
        { value: 'exploration', label: 'Explore & Learn', desc: 'General knowledge and growth' }
      ]
    },
    {
      id: 'preferredFormat',
      title: 'Preferred learning resources?',
      subtitle: 'We\'ll curate resources that match your preference',
      icon: Code,
      options: [
        { value: 'free-only', label: 'Free Resources Only', desc: 'YouTube, docs, free courses' },
        { value: 'paid-ok', label: 'Paid Courses OK', desc: 'Udemy, Coursera, etc.' },
        { value: 'bootcamp', label: 'Bootcamp/Structured', desc: 'Formal structured programs' },
        { value: 'no-preference', label: 'No Preference', desc: 'Whatever works best' }
      ]
    },
    {
      id: 'techStack',
      title: 'Target Tech Stack / Languages',
      subtitle: 'Optional - e.g., Java, Python, React, AWS',
      icon: Code,
      type: 'textarea',
      placeholder: 'E.g., I want the roadmap to focus strictly on Java and Spring Boot. Or: I prefer learning MERN stack.'
    },
    {
      id: 'constraints',
      title: 'Any specific constraints?',
      subtitle: 'Optional - helps us customize better',
      icon: Briefcase,
      type: 'textarea',
      placeholder: 'E.g., Working full-time, no coding background, prefer evening learning, need certifications for visa, etc.'
    }
  ];

  const handleOptionSelect = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));

    // Auto-advance to next question
    if (currentStep < questions.length - 1) {
      setTimeout(() => {
        setCurrentStep(currentStep + 1);
      }, 300);
    }
  };

  const handleTextareaChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    onSubmit(answers);
  };

  const progress = ((currentStep + 1) / questions.length) * 100;
  const currentQuestion = questions[currentStep];
  const isLastStep = currentStep === questions.length - 1;
  const canProceed = answers[currentQuestion.id] || currentQuestion.type === 'textarea';

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-3xl bg-[#0F131F] border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-b border-white/10 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-white font-mono mb-2">
                    Personalize Your Roadmap
                  </h2>
                  <p className="text-slate-400 font-mono text-sm">
                    For: <span className="text-emerald-400">{careerPath}</span>
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/10 rounded-lg transition-all"
                >
                  <X className="w-6 h-6 text-slate-400" />
                </button>
              </div>

              {/* Progress Bar */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400 font-mono">
                    Question {currentStep + 1} of {questions.length}
                  </span>
                  <span className="text-emerald-400 font-mono font-bold">
                    {Math.round(progress)}%
                  </span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-2">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.3 }}
                    className="bg-gradient-to-r from-emerald-500 to-teal-500 h-2 rounded-full"
                  />
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-8 min-h-[400px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  {/* Question */}
                  <div className="mb-8">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 flex items-center justify-center border border-emerald-500/30">
                        <currentQuestion.icon className="w-6 h-6 text-emerald-400" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white font-mono">
                          {currentQuestion.title}
                        </h3>
                        <p className="text-slate-400 text-sm font-mono">
                          {currentQuestion.subtitle}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Options or Textarea */}
                  {currentQuestion.type === 'textarea' ? (
                    <textarea
                      value={answers[currentQuestion.id] || ''}
                      onChange={(e) => handleTextareaChange(currentQuestion.id, e.target.value)}
                      placeholder={currentQuestion.placeholder}
                      className="w-full bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-white font-mono text-sm focus:border-emerald-500 focus:outline-none resize-none"
                      rows={6}
                    />
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {currentQuestion.options.map((option) => {
                        const isSelected = answers[currentQuestion.id] === option.value;
                        return (
                          <motion.button
                            key={option.value}
                            onClick={() => handleOptionSelect(currentQuestion.id, option.value)}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className={`text-left p-5 rounded-xl border-2 transition-all ${isSelected
                                ? 'bg-emerald-500/20 border-emerald-500'
                                : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                              }`}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="font-bold text-white font-mono">
                                {option.label}
                              </div>
                              {isSelected && (
                                <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                              )}
                            </div>
                            <p className="text-sm text-slate-400 font-mono">
                              {option.desc}
                            </p>
                          </motion.button>
                        );
                      })}
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Footer */}
            <div className="bg-slate-800/50 border-t border-white/10 p-6 flex items-center justify-between">
              <button
                onClick={handleBack}
                disabled={currentStep === 0}
                className="px-6 py-2 text-slate-400 hover:text-white font-mono font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                ← Back
              </button>

              <div className="flex items-center space-x-3">
                <button
                  onClick={onClose}
                  className="px-6 py-2 text-slate-400 hover:text-white font-mono font-semibold transition-all"
                >
                  Cancel
                </button>

                {isLastStep ? (
                  <button
                    onClick={handleSubmit}
                    disabled={!canProceed}
                    className="px-8 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-mono font-bold hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    Generate Roadmap →
                  </button>
                ) : (
                  <button
                    onClick={() => setCurrentStep(currentStep + 1)}
                    disabled={!canProceed}
                    className="px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-mono font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next →
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default RoadmapRefinementModal;

